import { useEffect, useMemo } from 'react';
import { useOutstandingCharges } from '../hooks/useTallyQueries';
import { usePaymentStore, useUIStore } from '../store';
import { LoadingSpinner, ErrorMessage, Button } from './ui';
import { SelectableChargesList } from './SelectableChargesList';
// import { EffectiveBalanceDisplay } from './EffectiveBalanceDisplay';
import { formatCurrency } from '../utils/formatters';
import type { Charge } from '../types';

export const Dashboard = () => {
  // const { user } = useAuthStore(); // Removed unused variable
  const { setActiveView } = useUIStore();
  const { setAvailableCharges, selectedCharge, setSelectedCharge } = usePaymentStore();
  
  const { 
    data: outstanding, 
    isLoading, 
    error, 
    refetch 
  } = useOutstandingCharges();

  // Flatten charges from all categories (memoized to prevent unnecessary re-renders)
  const allCharges = useMemo(() => {
    return outstanding ? Object.values(outstanding.chargesByCategory || {}).flat() : [];
  }, [outstanding]);

  // Set available charges in store when component loads or charges change
  useEffect(() => {
    if (allCharges.length > 0) {
      console.log('Dashboard: Setting available charges in store:', allCharges.length);
      setAvailableCharges(allCharges);
    }
  }, [allCharges]); // Remove setAvailableCharges from dependencies to prevent infinite loop
  
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-600">Loading your charges...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <ErrorMessage
          title="Failed to Load Charges"
          message={error.message}
          action={
            <Button onClick={() => refetch()} variant="outline" size="small">
              Try Again
            </Button>
          }
        />
      </div>
    );
  }
  
  if (!outstanding || allCharges.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-xl font-semibold text-green-600 mb-2">All Clear!</h2>
          <p className="text-gray-600 mb-4">You have no outstanding charges.</p>
          <div className="flex justify-center">
            <Button 
              onClick={() => setActiveView('history')}
              variant="outline"
            >
              View Payment History
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleChargeSelect = (charge: Charge) => {
    setSelectedCharge(charge);
  };

  const handleStartPayment = () => {
    if (selectedCharge) {
      setActiveView('payment');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="p-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              Outstanding Charges
            </h1>
            <p className="text-sm text-gray-600">
              Data sync frequency: Daily basis
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Sync Information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <div className="flex items-start">
            <div className="text-yellow-600 text-sm mr-2">⚠️</div>
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">Data Sync Information</p>
              <p>
                Receipts and transaction data are synced with Tally on a daily basis. 
                You can view transaction status in payment history, but complete data 
                synchronization occurs according to the scheduled frequency.
              </p>
            </div>
          </div>
        </div>

        {/* Effective Balance Display */}
        {/* <EffectiveBalanceDisplay className="mb-4" /> */}

        {/* Outstanding Summary - Backend Data Only */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Outstanding Balance</p>
            <p className="text-2xl font-semibold text-gray-700">
              {formatCurrency(outstanding.totalOutstanding)}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {allCharges.length} charge{allCharges.length !== 1 ? 's' : ''} from Tally
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Data from backend only
            </p>
            <div className="mt-3 flex justify-center">
              <Button 
                onClick={() => setActiveView('history')}
                variant="outline"
                size="small"
              >
                View Payment History
              </Button>
            </div>
          </div>
        </div>

        {/* Charges List */}
        <div className="bg-white rounded-lg shadow-sm border mb-4">
          <div className="p-4 border-b">
            <h3 className="font-medium text-gray-900">Select a Charge to Pay</h3>
            <p className="text-sm text-gray-600 mt-1">
              <span className="inline-flex items-center">
                <span className="w-3 h-3 border-2 border-gray-400 rounded-full mr-2"></span>
                Tap any charge below to select it for payment
              </span>
            </p>
          </div>
          <SelectableChargesList 
            charges={allCharges.filter(charge => charge.outstandingAmount > 0)}
            selectedCharge={selectedCharge}
            onChargeSelect={handleChargeSelect}
          />
        </div>

        {/* Pay Button - only show when charge is selected */}
        {selectedCharge && (
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="mb-3 text-center">
              <p className="text-sm text-gray-600">Selected Charge:</p>
              <p className="font-medium text-gray-900">{selectedCharge.chargeName}</p>
            </div>
            <Button
              onClick={handleStartPayment}
              variant="primary"
              size="large"
              className="w-full h-14 text-lg font-bold"
            >
              Proceed to Payment • {formatCurrency(selectedCharge.outstandingAmount)}
            </Button>
            <p className="text-xs text-gray-500 text-center mt-2">
              Choose payment type in the next screen
            </p>
          </div>
        )}
        
        {/* Instruction when no charge selected */}
        {!selectedCharge && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600">
              Select a charge from the list above to proceed with payment
            </p>
          </div>
        )}
      </div>
    </div>
  );
};